import './styles.scoped.css'
import {Fragment, useEffect, useState} from 'react'
import {Typography, Button, TextField, Link, Select, MenuItem, FormControl, FormHelperText, IconButton, Skeleton} from '@mui/material'
import {ArrowBack, UploadFile, Upload, Edit, Delete} from '@mui/icons-material'
import {useForm, Controller} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {accountApi} from '../../services'
import {useDropzone} from 'react-dropzone'
import {phoneRegex, emailRegex, parseError, usStates} from '../../utils/helpers'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {useNavigate, Link as LinkRoute} from 'react-router-dom'
import {useMobile} from '../../hooks'
import {AddressAutocompleteField, SkeletonContainer, FieldSkeleton, ButtonSkeleton} from '../../components'

const Label = ({children, required}) => (
    <Typography variant="inputLabel" color="text.secondary">
        {children}
        {required && (
            <>
                {' '}
                <Typography variant="inputLabel" color="error">
                    *
                </Typography>
            </>
        )}
    </Typography>
)

// The form laid out as it will be, labels and all, while the organization loads.
const FormSkeleton = ({mobile}) => {
    const fields = list =>
        list.map(([text, required]) => (
            <Fragment key={text}>
                <br />
                <br />
                <Label required={required}>{text}</Label>
                <FieldSkeleton label={false} />
            </Fragment>
        ))

    return (
        <SkeletonContainer>
            <Typography variant="overline" color="text.secondary">
                GENERAL INFORMATION
            </Typography>
            <br />
            <br />
            <div className="form" style={{display: mobile ? 'block' : 'flex'}}>
                <div className="form-left">
                    <Label>Healthcare Logo</Label>
                    {mobile ? (
                        <>
                            <br />
                            <ButtonSkeleton width={140} />
                            <br />
                            <br />
                        </>
                    ) : (
                        <Skeleton variant="rounded" height={150} sx={{minWidth: 300, borderRadius: '1px'}} />
                    )}
                </div>
                <div className="form-right">
                    <Label>Healthcare Name</Label>
                    <FieldSkeleton label={false} />
                </div>
            </div>
            {fields([['Healthcare Email'], ['Healthcare Phone Number']])}
            <br />
            <br />
            <Typography variant="overline" color="text.secondary">
                ADDRESS
            </Typography>
            {fields([['Street Address Line 1', true], ['Street Address Line 2']])}
            <br />
            <br />
            <div className="form">
                <div className="form-left">
                    <Label required>City</Label>
                    <FieldSkeleton label={false} />
                </div>
                <div className="form-right">
                    <Label required>State</Label>
                    <FieldSkeleton label={false} />
                </div>
            </div>
            {fields([['Zip Code', true]])}
            <br />
            <br />
            <br />
            <Typography variant="overline" color="text.secondary">
                CONTACT PERSON
            </Typography>
            {fields([['Name'], ['Contact Person Phone Number'], ['Contact Person Email']])}
        </SkeletonContainer>
    )
}
export default () => {
    const {setLoading, setMessageBox} = useGlobalStates()
    const {mobile} = useMobile()
    const navigate = useNavigate()
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                name: yup.string(),
                email: yup.string().matches(emailRegex, 'Invalid email address'),
                phone: yup.string().test('phone-optional', 'Invalid phone number', value => {
                    if (!value || value.trim() === '') return true
                    return phoneRegex.test(value)
                }),
                address: yup.object().shape({
                    city: yup.string().required('City is required'),
                    state: yup.string().required('State is required'),
                    street: yup.string().required('Street is required'),
                    street2: yup.string(),
                    zip: yup.string().required('ZIP code is required'),
                }),
                contact_name: yup.string(),
                contact_phone: yup.string().test('phone-optional', 'Invalid phone number', value => {
                    if (!value || value.trim() === '') return true
                    return phoneRegex.test(value)
                }),
                contact_email: yup.string().test('email-optional', 'Invalid email address', value => {
                    if (!value || value.trim() === '') return true
                    return emailRegex.test(value)
                }),
            }),
        ),
    })
    const [file, setFile] = useState(null)
    const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
        // No SVG: it can carry script, and the mobile app can't render it.
        accept: {'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/gif': ['.gif'], 'image/webp': ['.webp']},
        noClick: true,
        multiple: false,
        maxSize: 3 * 1024 * 1024,
        onDrop: acceptedFiles => {
            setFile(acceptedFiles[0])
            setLogoUrl(URL.createObjectURL(acceptedFiles[0]))
        },
    })
    const [logoUrl, setLogoUrl] = useState(null)
    const [skeleton, setSkeleton] = useState(true)

    useEffect(() => {
        setSkeleton(true)
        accountApi
            .getOrganization()
            .then(({data: {data}}) => {
                setSkeleton(false)
                setLogoUrl(data?.logo_url || null)
                Object.keys(form.getValues()).forEach(field => {
                    if (typeof data[field] === 'object' && data[field] !== null) {
                        Object.keys(data[field]).forEach(nestedField => {
                            form.setValue(`${field}.${nestedField}`, data[field][nestedField])
                        })
                    } else {
                        form.setValue(field, data[field])
                    }
                })
            })
            .catch(error => {
                setSkeleton(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }, [])

    useEffect(() => {
        if (!file) return
        const formData = new FormData()
        formData.append('file', file)
        setLoading(true)
        accountApi
            .uploadOrganizationLogo(formData)
            .then(({data: {data}}) => {
                setLoading(false)
                setLogoUrl(data?.logo_url || null)
            })
            .catch(() => {
                setLoading(false)
                setFile(null)
                setMessageBox({
                    status: 'error',
                    title: 'Logo Upload Failed',
                    description: 'There was an error uploading your logo. Please try again later.',
                })
            })
    }, [file])

    const removeFile = () => {
        setLoading(true)
        accountApi
            .removeOrganizationLogo()
            .then(() => {
                setLoading(false)
                setFile(null)
                setLogoUrl(null)
            })
            .catch(() => {
                setLoading(false)
                setMessageBox({
                    status: 'error',
                    title: 'Logo Removal Failed',
                    description: 'There was an error removing your logo. Please try again later.',
                })
            })
    }

    const selectAddress = selected => {
        form.setValue('address.street2', '')
        form.setValue('address.city', selected?.city)
        form.setValue('address.state', selected?.state)
        form.setValue('address.zip', selected?.postcode)
    }

    const updateOrganization = data => {
        setLoading(true)
        accountApi
            .updateOrganization(data)
            .then(() => {
                setLoading(false)
                setMessageBox({
                    status: 'success',
                    title: 'Organization Updated Successfully',
                    description: 'Your organization has been updated successfully.',
                })
                navigate('/organization-settings')
            })
            .catch(() => {
                setLoading(false)
                setMessageBox({
                    status: 'error',
                    title: 'Organization Update Failed',
                    description: 'There was an error updating your organization. Please try again later.',
                })
            })
    }

    return (
        <>
            {mobile ? (
                <>
                    <div className="header">
                        <LinkRoute to="/organization-settings">
                            <IconButton>
                                <ArrowBack />
                            </IconButton>
                        </LinkRoute>
                        <Typography variant="h6" color="text.primary">
                            Edit Organization Settings
                        </Typography>
                    </div>
                    <div className="actions">
                        <Button variant="outlined" onClick={() => navigate('/organization-settings')} style={{flex: 1}}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={form.handleSubmit(updateOrganization)} style={{flex: 1}}>
                            Save Changes
                        </Button>
                    </div>
                    <br />
                </>
            ) : (
                <>
                    <Typography variant="body1" color="text.secondary">
                        Settings /{' '}
                        <LinkRoute className="link" to="/organization-settings">
                            Organization Settings
                        </LinkRoute>{' '}
                        / Edit Organization Settings
                    </Typography>
                    <br />
                    <div className="header">
                        <div className="back-container">
                            <LinkRoute className="back-button" to="/organization-settings">
                                <ArrowBack style={{color: '#ffffff'}} fontSize="small" />
                            </LinkRoute>
                            <Typography variant="h5" color="text.primary">
                                Edit Organization Settings
                            </Typography>
                        </div>
                        <div className="actions">
                            <Button variant="outlined" onClick={() => navigate('/organization-settings')}>
                                Cancel
                            </Button>
                            <Button variant="contained" onClick={form.handleSubmit(updateOrganization)}>
                                Save Changes
                            </Button>
                        </div>
                    </div>
                </>
            )}
            <br />
            <br />
            {skeleton && <FormSkeleton mobile={mobile} />}
            <div style={{display: skeleton ? 'none' : 'block'}}>
                <Typography variant="overline" color="text.secondary">
                    GENERAL INFORMATION
                </Typography>
                <br />
                <br />
                <div className="form" style={{display: mobile ? 'block' : 'flex'}}>
                    <div className="form-left">
                        <Typography variant="inputLabel" color="text.secondary">
                            Healthcare Logo
                        </Typography>
                        <div style={{display: logoUrl ? 'flex' : 'none', flexDirection: 'row', alignItems: 'center', gap: 8}}>
                            <img src={logoUrl} height={102} />
                            <Button variant="outlined" startIcon={<Edit />} onClick={open}>
                                Replace
                            </Button>
                            <Button variant="outlined" startIcon={<Delete />} color="error" onClick={removeFile}>
                                Remove
                            </Button>
                            <br />
                        </div>
                        <div
                            {...getRootProps()}
                            style={{
                                width: '100%',
                                backgroundColor: isDragActive ? '#e3f2fd' : '#fbfbfb',
                                display: logoUrl || mobile ? 'none' : 'block',
                                borderColor: isDragActive ? '#3f51b5' : '#0000001F',
                                border: '1px dashed #0000001F',
                                borderRadius: 1,
                                height: 150,
                                minWidth: 300,
                                boxSizing: 'border-box',
                                textAlign: 'center',
                            }}>
                            <input {...getInputProps()} />
                            <br />
                            <UploadFile style={{fontSize: 26}} color="primary" />
                            <Typography variant="body1" color="text.primary" style={{marginTop: 10, marginBottom: 10}}>
                                <Link underline="always" onClick={open} style={{cursor: 'pointer'}}>
                                    {'Click to upload '}
                                </Link>
                                or drag and drop
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                PNG, JPG, GIF or WebP (max. 3MB)
                            </Typography>
                        </div>
                        {!logoUrl && mobile && (
                            <>
                                <br />
                                <Button variant="outlined" startIcon={<Upload />} onClick={open}>
                                    Upload Logo
                                </Button>
                                <br />
                                <br />
                            </>
                        )}
                    </div>
                    <div className="form-right">
                        <Typography variant="inputLabel" color="text.secondary">
                            Healthcare Name
                        </Typography>
                        <TextField fullWidth disabled {...form.register('name')} />
                    </div>
                </div>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Healthcare Email
                </Typography>
                <TextField fullWidth disabled {...form.register('email')} />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Healthcare Phone Number
                </Typography>
                <TextField
                    placeholder="Enter your healthcare phone number"
                    fullWidth
                    {...form.register('phone')}
                    error={!!form.formState.errors['phone']}
                    helperText={form.formState.errors['phone']?.message}
                />
                <br />
                <br />
                <Typography variant="overline" color="text.secondary">
                    ADDRESS
                </Typography>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Street Address Line 1{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <AddressAutocompleteField
                    control={form.control}
                    name="address.street"
                    error={!!form.formState.errors?.address?.street}
                    helperText={form.formState.errors?.address?.street?.message}
                    onSelect={selectAddress}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Street Address Line 2
                </Typography>
                <TextField placeholder="Enter your street address line 2" fullWidth {...form.register('address.street2')} />
                <br />
                <br />
                <div className="form">
                    <div className="form-left">
                        <Typography variant="inputLabel" color="text.secondary">
                            City{' '}
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        </Typography>
                        <TextField
                            placeholder="Enter your city"
                            fullWidth
                            {...form.register('address.city')}
                            error={!!form.formState.errors?.address?.city}
                            helperText={form.formState.errors?.address?.city?.message}
                        />
                    </div>
                    <div className="form-right">
                        <Typography variant="inputLabel" color="text.secondary">
                            State{' '}
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        </Typography>
                        <FormControl fullWidth error={!!form.formState.errors?.address?.state}>
                            <Controller
                                {...form.register('address.state')}
                                control={form.control}
                                render={({field}) => (
                                    <Select
                                        {...field}
                                        value={field.value ?? ''}
                                        onChange={e => field.onChange(e.target.value)}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                },
                                            },
                                            anchorOrigin: {
                                                vertical: 'top',
                                                horizontal: 'left',
                                            },
                                            transformOrigin: {
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            },
                                        }}
                                        displayEmpty
                                        renderValue={selected => {
                                            if (selected === '') {
                                                return <span style={{color: '#aaa'}}>Select your state</span>
                                            }
                                            return selected
                                        }}>
                                        {usStates.map(item => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            <FormHelperText>{form.formState.errors?.address?.state?.message}</FormHelperText>
                        </FormControl>
                    </div>
                </div>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Zip Code{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <TextField
                    placeholder="Enter your zip code"
                    fullWidth
                    {...form.register('address.zip')}
                    error={!!form.formState.errors?.address?.zip}
                    helperText={form.formState.errors?.address?.zip?.message}
                />
                <br />
                <br />
                <br />
                <Typography variant="overline" color="text.secondary">
                    CONTACT PERSON
                </Typography>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Name
                </Typography>
                <TextField placeholder="Enter your name" fullWidth {...form.register('contact_name')} />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Contact Person Phone Number
                </Typography>
                <TextField placeholder="Enter your contact person phone number" fullWidth {...form.register('contact_phone')} />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Contact Person Email
                </Typography>
                <TextField placeholder="Enter your email" fullWidth {...form.register('contact_email')} />
            </div>
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
        </>
    )
}
